import React, { useEffect, useRef, useState } from 'react';
import { Application, Container, Graphics, Text, TextStyle } from 'pixi.js';
import { LetterStroke, StrokePoint } from '../../core/writing/letterStrokesData';
import { speechProvider } from '../../core/speech/speechProvider';

export interface PixiLetterTracingCanvasProps {
  letter: string;
  isUppercase: boolean;
  strokes: LetterStroke[];
  onLetterCompleted: () => void;
  onStrokeCompleted?: (completedIndex: number, total: number) => void;
  onStrokeIncomplete?: (strokeIndex: number, currentStroke: LetterStroke) => void;
  className?: string;
}

export const PixiLetterTracingCanvas: React.FC<PixiLetterTracingCanvasProps> = ({
  letter,
  isUppercase,
  strokes,
  onLetterCompleted,
  onStrokeCompleted,
  onStrokeIncomplete,
  className = '',
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const appRef = useRef<Application | null>(null);

  const [currentStrokeIndex, setCurrentStrokeIndex] = useState(0);
  const [completedStrokes, setCompletedStrokes] = useState<number[]>([]);
  const [isDemoRunning, setIsDemoRunning] = useState(false);
  const [isDone, setIsDone] = useState(false);

  // References for Pixi state that the ticker and event handlers need
  const pixiStateRef = useRef({
    currentStrokeIndex: 0,
    currentWaypointIndex: 1,
    completedStrokes: [] as number[],
    isTracing: false,
    userTrail: [] as { x: number; y: number }[],
    demoProgress: 0,
    isDemoing: false,
    width: 320,
    height: 320,
    pulseTime: 0,
    sparkles: [] as { x: number; y: number; vx: number; vy: number; alpha: number; color: number; size: number }[],
    completionNotified: false,
  });

  // Keep ref synchronized with React state
  useEffect(() => {
    pixiStateRef.current.currentStrokeIndex = currentStrokeIndex;
    pixiStateRef.current.completedStrokes = completedStrokes;
  }, [currentStrokeIndex, completedStrokes]);

  // Reset when letter or case changes
  useEffect(() => {
    setCurrentStrokeIndex(0);
    setCompletedStrokes([]);
    setIsDone(false);
    setIsDemoRunning(false);
    pixiStateRef.current.currentStrokeIndex = 0;
    pixiStateRef.current.completedStrokes = [];
    pixiStateRef.current.userTrail = [];
    pixiStateRef.current.isDemoing = false;
    pixiStateRef.current.completionNotified = false;
  }, [letter, isUppercase]);

  useEffect(() => {
    let isCancelled = false;
    let pixiApp: Application | null = null;

    const initCanvas = async () => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const width = Math.min(360, Math.max(280, rect.width || 320));
      const height = width; // Keep 1:1 aspect ratio
      pixiStateRef.current.width = width;
      pixiStateRef.current.height = height;

      pixiApp = new Application();
      await pixiApp.init({
        width,
        height,
        backgroundColor: 0xfffefb,
        resolution: Math.min(window.devicePixelRatio || 1, 2),
        autoDensity: true,
        antialias: true,
      });

      if (isCancelled || !containerRef.current) {
        pixiApp.destroy(true, { children: true });
        return;
      }

      appRef.current = pixiApp;
      containerRef.current.innerHTML = '';
      containerRef.current.appendChild(pixiApp.canvas);

      // Main Layer Containers
      const stage = pixiApp.stage;
      stage.removeChildren();
      stage.eventMode = 'static';
      stage.hitArea = pixiApp.screen;

      const bgLayer = new Container();
      const ghostLayer = new Container();
      const completedLayer = new Container();
      const activeGuideLayer = new Container();
      const userTrailLayer = new Graphics();
      const demoHandLayer = new Container();
      const sparklesLayer = new Graphics();

      stage.addChild(bgLayer);
      stage.addChild(ghostLayer);
      stage.addChild(completedLayer);
      stage.addChild(activeGuideLayer);
      stage.addChild(userTrailLayer);
      stage.addChild(sparklesLayer);
      stage.addChild(demoHandLayer);

      // Helper to convert normalized (0..100) to canvas coordinates
      const toCanvasX = (pct: number) => (pct / 100) * width;
      const toCanvasY = (pct: number) => (pct / 100) * height;

      // 1. Draw Paper Background and Handwriting Guide Lines
      const drawBackground = () => {
        bgLayer.removeChildren();
        const bg = new Graphics();
        // Rounded card background
        bg.roundRect(0, 0, width, height, 24);
        bg.fill(0xfffef9);
        bg.stroke({ width: 3, color: 0xfef08a });

        // Handwriting Lines
        // Top line (ascender / ceiling line)
        const yTop = toCanvasY(15);
        bg.moveTo(16, yTop);
        bg.lineTo(width - 16, yTop);
        bg.stroke({ width: 1.5, color: 0xcbd5e1 });

        // Midline (dashed waistline)
        const yMid = toCanvasY(50);
        for (let x = 16; x < width - 16; x += 14) {
          bg.moveTo(x, yMid);
          bg.lineTo(Math.min(x + 8, width - 16), yMid);
        }
        bg.stroke({ width: 2, color: 0x93c5fd });

        // Baseline (solid ground line)
        const yBase = toCanvasY(85);
        bg.moveTo(16, yBase);
        bg.lineTo(width - 16, yBase);
        bg.stroke({ width: 2.5, color: 0x64748b });

        // Descender line (dashed red)
        const yDesc = toCanvasY(98);
        for (let x = 16; x < width - 16; x += 14) {
          bg.moveTo(x, yDesc);
          bg.lineTo(Math.min(x + 8, width - 16), yDesc);
        }
        bg.stroke({ width: 1.5, color: 0xfca5a5 });

        bgLayer.addChild(bg);
      };

      drawBackground();

      // 2. Draw Ghost Letter Template
      const drawGhostTemplate = () => {
        ghostLayer.removeChildren();
        const ghost = new Graphics();

        strokes.forEach((stroke) => {
          if (stroke.points.length < 2) {
            // Single dot
            const p = stroke.points[0];
            ghost.circle(toCanvasX(p.x), toCanvasY(p.y), 12);
            ghost.fill(0xe2e8f0);
            return;
          }

          ghost.moveTo(toCanvasX(stroke.points[0].x), toCanvasY(stroke.points[0].y));
          for (let i = 1; i < stroke.points.length; i++) {
            ghost.lineTo(toCanvasX(stroke.points[i].x), toCanvasY(stroke.points[i].y));
          }
          ghost.stroke({ width: 26, color: 0xe2e8f0, cap: 'round', join: 'round' });
        });

        ghostLayer.addChild(ghost);
      };

      drawGhostTemplate();

      // 3. Render Completed Strokes
      const renderCompletedStrokes = () => {
        completedLayer.removeChildren();
        const g = new Graphics();

        pixiStateRef.current.completedStrokes.forEach((idx) => {
          const s = strokes[idx];
          if (!s) return;

          if (s.points.length < 2) {
            const p = s.points[0];
            g.circle(toCanvasX(p.x), toCanvasY(p.y), 14);
            g.fill(0x10b981);
            return;
          }

          g.moveTo(toCanvasX(s.points[0].x), toCanvasY(s.points[0].y));
          for (let i = 1; i < s.points.length; i++) {
            g.lineTo(toCanvasX(s.points[i].x), toCanvasY(s.points[i].y));
          }
          g.stroke({ width: 22, color: 0x10b981, cap: 'round', join: 'round' });

          // Draw small check star at end point
          const endPoint = s.points[s.points.length - 1];
          g.circle(toCanvasX(endPoint.x), toCanvasY(endPoint.y), 6);
          g.fill(0xfbbf24);
        });

        completedLayer.addChild(g);
      };

      // Helper: Shortest distance from point (px, py) to line segment (x1, y1)-(x2, y2)
      const distToSegment = (
        px: number,
        py: number,
        x1: number,
        y1: number,
        x2: number,
        y2: number
      ) => {
        const dx = x2 - x1;
        const dy = y2 - y1;
        const l2 = dx * dx + dy * dy;
        if (l2 === 0) return Math.hypot(px - x1, py - y1);
        let t = ((px - x1) * dx + (py - y1) * dy) / l2;
        t = Math.max(0, Math.min(1, t));
        return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
      };

      // 4. Render Active Stroke Guidance (Numbered circles, arrows, pulse)
      const renderActiveGuide = () => {
        activeGuideLayer.removeChildren();
        const activeIdx = pixiStateRef.current.currentStrokeIndex;
        if (activeIdx >= strokes.length) return;

        const g = new Graphics();
        const pulse = Math.sin(pixiStateRef.current.pulseTime * 4) * 4;

        // Draw all stroke start numbers
        strokes.forEach((stroke, idx) => {
          const isCompleted = pixiStateRef.current.completedStrokes.includes(idx);
          const isActive = idx === activeIdx;
          const startPt = stroke.points[0];
          const cx = toCanvasX(startPt.x);
          const cy = toCanvasY(startPt.y);

          if (isActive && !pixiStateRef.current.isTracing) {
            // Pulsing highlight ring around active start point
            g.circle(cx, cy, 29 + pulse);
            g.stroke({ width: 5, color: 0xf59e0b, alpha: 0.8 });

            // Guide arrow line to the next point
            if (stroke.points.length > 1) {
              const nextPt = stroke.points[1];
              const nx = toCanvasX(nextPt.x);
              const ny = toCanvasY(nextPt.y);
              const dx = nx - cx;
              const dy = ny - cy;
              const len = Math.sqrt(dx * dx + dy * dy);
              if (len > 0) {
                const arrowDist = Math.min(36, len * 0.8);
                const ax = cx + (dx / len) * arrowDist;
                const ay = cy + (dy / len) * arrowDist;
                g.moveTo(cx, cy);
                g.lineTo(ax, ay);
                g.stroke({ width: 3.5, color: 0xf59e0b });
                // Arrow tip
                g.circle(ax, ay, 4.5);
                g.fill(0xf59e0b);
              }
            }
          }

          // Number Circle Badge
          g.circle(cx, cy, 22);
          g.fill(isCompleted ? 0x10b981 : isActive ? 0x2563eb : 0x94a3b8);
          g.stroke({ width: 3, color: 0xffffff });

          // Number Text
          const textStyle = new TextStyle({
            fontFamily: 'Fredoka, Arial, sans-serif',
            fontSize: 22,
            fontWeight: 'bold',
            fill: 0xffffff,
          });
          const text = new Text({
            text: isCompleted ? '✓' : stroke.label,
            style: textStyle,
          });
          text.anchor.set(0.5);
          text.x = cx;
          text.y = cy;
          activeGuideLayer.addChild(text);
        });

        // If currently tracing, show dynamic target guide on the next waypoint!
        if (pixiStateRef.current.isTracing) {
          const currentStroke = strokes[activeIdx];
          const wpIdx = pixiStateRef.current.currentWaypointIndex;
          if (currentStroke && wpIdx < currentStroke.points.length) {
            const targetPt = currentStroke.points[wpIdx];
            const wx = toCanvasX(targetPt.x);
            const wy = toCanvasY(targetPt.y);

            // Pulsing target halo
            g.circle(wx, wy, 16 + pulse);
            g.stroke({ width: 3, color: 0xf59e0b, alpha: 0.9 });
            g.circle(wx, wy, 6);
            g.fill(0xf59e0b);

            // Small star guide text above the target waypoint
            const targetTextStyle = new TextStyle({
              fontSize: 14,
            });
            const starText = new Text({
              text: '⭐',
              style: targetTextStyle,
            });
            starText.anchor.set(0.5);
            starText.x = wx;
            starText.y = wy - 18 - pulse * 0.5;
            activeGuideLayer.addChild(starText);
          }
        }

        activeGuideLayer.addChildAt(g, 0);
      };

      // 5. Add Sparkles Generator
      const createSparkles = (x: number, y: number, count = 8) => {
        const colors = [0xf59e0b, 0x3b82f6, 0x10b981, 0xec4899, 0xa855f7];
        for (let i = 0; i < count; i++) {
          const angle = Math.random() * Math.PI * 2;
          const speed = 1.5 + Math.random() * 3.5;
          pixiStateRef.current.sparkles.push({
            x,
            y,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            alpha: 1,
            color: colors[Math.floor(Math.random() * colors.length)],
            size: 3 + Math.random() * 5,
          });
        }
      };

      // Complete active stroke handler
      const completeCurrentStroke = () => {
        const activeIdx = pixiStateRef.current.currentStrokeIndex;
        const currentStroke = strokes[activeIdx];
        if (!currentStroke) return;

        const endPt = currentStroke.points[currentStroke.points.length - 1];
        const targetEndX = toCanvasX(endPt.x);
        const targetEndY = toCanvasY(endPt.y);

        // Success for this stroke!
        createSparkles(targetEndX, targetEndY, 18);
        speechProvider.playChime('tap');

        const nextCompleted = [...pixiStateRef.current.completedStrokes, activeIdx];
        pixiStateRef.current.completedStrokes = nextCompleted;
        setCompletedStrokes(nextCompleted);

        // CRITICAL: End active stroke tracing and reset user trail and waypoints
        // so that the next stroke CANNOT be triggered without lifting/starting at point 0!
        pixiStateRef.current.isTracing = false;
        pixiStateRef.current.userTrail = [];
        pixiStateRef.current.currentWaypointIndex = 1;

        const nextIdx = activeIdx + 1;
        pixiStateRef.current.currentStrokeIndex = nextIdx;
        setCurrentStrokeIndex(nextIdx);

        if (onStrokeCompleted) {
          onStrokeCompleted(nextIdx, strokes.length);
        }

        // Check if all strokes are done!
        if (nextIdx >= strokes.length) {
          setIsDone(true);
          createSparkles(width / 2, height / 2, 40);
          if (!pixiStateRef.current.completionNotified) {
            pixiStateRef.current.completionNotified = true;
            console.log('[WritingDebug] canvas completion callback', {
              letter,
              strokes: strokes.length,
              time: new Date().toISOString(),
            });
            onLetterCompleted();
          }
        }
      };

      stage.on('pointerdown', (e) => {
        if (pixiStateRef.current.isDemoing) return;
        const activeIdx = pixiStateRef.current.currentStrokeIndex;
        if (activeIdx >= strokes.length) return;

        const pos = e.getLocalPosition(stage);
        const currentStroke = strokes[activeIdx];
        const startPt = currentStroke.points[0];
        const sx = toCanvasX(startPt.x);
        const sy = toCanvasY(startPt.y);

        const dist = Math.hypot(pos.x - sx, pos.y - sy);
        // Generous start tolerance for young children (up to 48px radius)
        if (dist < 48) {
          // Check if this is a single dot stroke (e.g., dot on 'i' or 'j')
          const endPt = currentStroke.points[currentStroke.points.length - 1];
          const isDotStroke =
            currentStroke.points.length <= 2 &&
            Math.hypot(toCanvasX(endPt.x) - sx, toCanvasY(endPt.y) - sy) < 14;

          if (isDotStroke) {
            createSparkles(sx, sy, 14);
            completeCurrentStroke();
            return;
          }

          pixiStateRef.current.isTracing = true;
          pixiStateRef.current.userTrail = [{ x: pos.x, y: pos.y }];
          pixiStateRef.current.currentWaypointIndex = 1;
          createSparkles(pos.x, pos.y, 6);
        }
      });

      stage.on('pointermove', (e) => {
        if (!pixiStateRef.current.isTracing) return;
        const activeIdx = pixiStateRef.current.currentStrokeIndex;
        const currentStroke = strokes[activeIdx];
        if (!currentStroke) return;

        const pos = e.getLocalPosition(stage);
        const trail = pixiStateRef.current.userTrail;
        const last = trail[trail.length - 1];

        // Track user stroke path
        if (!last || Math.hypot(pos.x - last.x, pos.y - last.y) > 4) {
          const prevX = last ? last.x : pos.x;
          const prevY = last ? last.y : pos.y;
          trail.push({ x: pos.x, y: pos.y });

          if (Math.random() > 0.65) {
            createSparkles(pos.x, pos.y, 2);
          }

          // Sequential Waypoint Progression Check
          // The child must traverse each waypoint of the stroke in order
          let wpIdx = pixiStateRef.current.currentWaypointIndex;
          const points = currentStroke.points;
          const WAYPOINT_HIT_RADIUS = 48; // Forgiving 48px hit area for preschoolers

          while (wpIdx < points.length) {
            const targetPt = points[wpIdx];
            const tx = toCanvasX(targetPt.x);
            const ty = toCanvasY(targetPt.y);

            // Check distance from current position OR the movement segment from previous position
            const distDirect = Math.hypot(pos.x - tx, pos.y - ty);
            const distSeg = distToSegment(tx, ty, prevX, prevY, pos.x, pos.y);
            const effectiveDist = Math.min(distDirect, distSeg);

            if (effectiveDist < WAYPOINT_HIT_RADIUS) {
              // Reached this waypoint!
              createSparkles(tx, ty, 6);
              wpIdx++;
              pixiStateRef.current.currentWaypointIndex = wpIdx;
            } else {
              break;
            }
          }

          // If all waypoints have been completed in sequence:
          if (wpIdx >= points.length) {
            completeCurrentStroke();
          }
        }
      });

      const handlePointerEnd = () => {
        if (!pixiStateRef.current.isTracing) return;
        const activeIdx = pixiStateRef.current.currentStrokeIndex;
        const currentStroke = strokes[activeIdx];
        const hadDrawn = pixiStateRef.current.userTrail.length > 3;

        pixiStateRef.current.isTracing = false;
        pixiStateRef.current.userTrail = [];
        pixiStateRef.current.currentWaypointIndex = 1;

        if (hadDrawn && currentStroke && onStrokeIncomplete) {
          onStrokeIncomplete(activeIdx, currentStroke);
        }
      };

      stage.on('pointerup', handlePointerEnd);
      stage.on('pointerupoutside', handlePointerEnd);

      // 6. Animation Loop (Ticker)
      let elapsed = 0;
      pixiApp.ticker.add((ticker) => {
        elapsed += ticker.deltaTime * 0.05;
        pixiStateRef.current.pulseTime = elapsed;

        // Render Active Guide with pulse
        renderActiveGuide();
        renderCompletedStrokes();

        // Draw User Inking Trail
        userTrailLayer.clear();
        const trail = pixiStateRef.current.userTrail;
        if (trail.length > 1) {
          userTrailLayer.moveTo(trail[0].x, trail[0].y);
          for (let i = 1; i < trail.length; i++) {
            userTrailLayer.lineTo(trail[i].x, trail[i].y);
          }
          userTrailLayer.stroke({
            width: 22,
            color: 0x3b82f6,
            cap: 'round',
            join: 'round',
          });
        }

        // Draw & Update Sparkles
        sparklesLayer.clear();
        const sparkles = pixiStateRef.current.sparkles;
        for (let i = sparkles.length - 1; i >= 0; i--) {
          const s = sparkles[i];
          s.x += s.vx;
          s.y += s.vy;
          s.vy += 0.08; // gravity
          s.alpha -= 0.025;

          if (s.alpha <= 0) {
            sparkles.splice(i, 1);
          } else {
            sparklesLayer.circle(s.x, s.y, s.size);
            sparklesLayer.fill({ color: s.color, alpha: s.alpha });
          }
        }
      });
    };

    initCanvas();

    return () => {
      isCancelled = true;
      if (appRef.current) {
        try {
          appRef.current.destroy(true, { children: true });
        } catch (e) {
          console.warn('Canvas cleanup error:', e);
        }
        appRef.current = null;
      }
    };
  }, [letter, isUppercase, strokes]);

  // Demo Animation Handlers
  const handlePlayDemo = () => {
    if (isDemoRunning || !appRef.current) return;
    setIsDemoRunning(true);
    speechProvider.playChime('tap');
    speechProvider.speak(`Perhatikan Rara menulis huruf ${letter}!`, { rate: 0.88 });

    const app = appRef.current;
    const stage = app.stage;
    const toCanvasX = (pct: number) => (pct / 100) * pixiStateRef.current.width;
    const toCanvasY = (pct: number) => (pct / 100) * pixiStateRef.current.height;

    // Create Demo Pointer Sprite
    const pointerContainer = new Container();
    const pencilG = new Graphics();
    pencilG.circle(0, 0, 18);
    pencilG.fill(0xf59e0b);
    pencilG.stroke({ width: 3, color: 0xffffff });
    const pencilIcon = new Text({
      text: '✏️',
      style: new TextStyle({ fontSize: 20 }),
    });
    pencilIcon.anchor.set(0.5);
    pointerContainer.addChild(pencilG);
    pointerContainer.addChild(pencilIcon);
    stage.addChild(pointerContainer);

    let strokeStep = 0;
    let pointStep = 0;
    let t = 0;

    const demoTicker = () => {
      if (strokeStep >= strokes.length) {
        app.ticker.remove(demoTicker);
        stage.removeChild(pointerContainer);
        setIsDemoRunning(false);
        speechProvider.speak('Sekarang giliranmu, ayo coba tulis!', { rate: 0.88 });
        return;
      }

      const s = strokes[strokeStep];
      if (s.points.length < 2) {
        strokeStep++;
        return;
      }

      const p1 = s.points[pointStep];
      const p2 = s.points[pointStep + 1];
      if (!p2) {
        strokeStep++;
        pointStep = 0;
        t = 0;
        return;
      }

      t += 0.04;
      const curX = toCanvasX(p1.x) + (toCanvasX(p2.x) - toCanvasX(p1.x)) * t;
      const curY = toCanvasY(p1.y) + (toCanvasY(p2.y) - toCanvasY(p1.y)) * t;

      pointerContainer.x = curX;
      pointerContainer.y = curY;

      if (t >= 1) {
        t = 0;
        pointStep++;
      }
    };

    app.ticker.add(demoTicker);
  };

  const handleReset = () => {
    speechProvider.playChime('tap');
    setCurrentStrokeIndex(0);
    setCompletedStrokes([]);
    setIsDone(false);
    pixiStateRef.current.currentStrokeIndex = 0;
    pixiStateRef.current.currentWaypointIndex = 1;
    pixiStateRef.current.isTracing = false;
    pixiStateRef.current.completedStrokes = [];
    pixiStateRef.current.userTrail = [];
    speechProvider.speak('Ayo coba tulis lagi dari nomor satu!', { rate: 0.9 });
  };

  return (
    <div className={`flex flex-col items-center select-none ${className}`}>
      {/* Interactive Pixi Canvas Container */}
      <div className="relative shadow-lg rounded-3xl overflow-hidden border-4 border-amber-300 bg-white">
        <div
          ref={containerRef}
          className="w-[300px] h-[300px] sm:w-[340px] sm:h-[340px] touch-none flex items-center justify-center cursor-crosshair"
        />

        {/* Success Overlay Badge */}
        {isDone && (
          <div className="absolute inset-0 bg-emerald-500/20 backdrop-blur-2xs flex flex-col items-center justify-center p-4 text-center pointer-events-none animate-in fade-in zoom-in-95 duration-300">
            <div className="w-20 h-20 rounded-full bg-linear-to-br from-amber-400 to-yellow-400 text-white flex items-center justify-center text-4xl shadow-xl ring-4 ring-white mb-2 animate-bounce">
              ⭐
            </div>
            <span className="bg-white/95 px-4 py-1.5 rounded-full text-[#1e293b] font-display font-black text-sm shadow-md border-2 border-emerald-400">
              Hebat! Huruf {letter} Selesai!
            </span>
          </div>
        )}
      </div>

      {/* Under-Canvas Controls */}
      <div className="flex items-center justify-center gap-3 mt-4 w-full max-w-[340px]">
        <button
          onClick={handlePlayDemo}
          disabled={isDemoRunning}
          className="flex-1 py-2.5 px-3 rounded-2xl bg-amber-100 hover:bg-amber-200 active:scale-95 disabled:opacity-50 text-amber-950 font-display font-black text-xs sm:text-sm flex items-center justify-center gap-1.5 border border-amber-300 shadow-xs transition cursor-pointer"
        >
          <span>{isDemoRunning ? '⏳' : '▶️'}</span>
          <span>{isDemoRunning ? 'Memperagakan...' : 'Lihat Contoh'}</span>
        </button>

        <button
          onClick={handleReset}
          className="py-2.5 px-4 rounded-2xl bg-gray-100 hover:bg-gray-200 active:scale-95 text-gray-700 font-display font-black text-xs sm:text-sm flex items-center justify-center gap-1.5 border border-gray-300 shadow-xs transition cursor-pointer"
        >
          <span>🔄</span>
          <span>Ulangi</span>
        </button>
      </div>
    </div>
  );
};
